import {
  Box,
  Header,
  SpaceBetween,
  Cards,
  Link,
  ColumnLayout,
  Pagination,
  CollectionPreferences,
  CollectionPreferencesProps,
  Table,
  Button,
  Modal,
  Container,
  Grid,
} from '@cloudscape-design/components';

import { DashboardViewLayout } from '../../../components/DashboardViewLayout';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { LoadingOrError } from '../../../components/Loading';
import { Place, PlaceBasic, PlaceList, PlaceRequest } from '../../../common/types';
import { useMemo, useState } from 'react';
import { getList, removePlacesFromList } from '../../../common/api/lists';
import { PlaceTable } from '../../../components/dashboard/PlaceTable';
import { useUrlSortingState } from '../../../common/filtering/filters';

export default function View() {
  const navigate = useNavigate();
  const resourceName = 'Lists';
  const endpointName = 'lists';
  const { uuid } = useParams({ from: '/dashboard/lists_/$uuid' });

  // wait for the meta query to finish before proceeding
  const { isPending, isFetching, error, data, refetch } = useQuery({
    queryKey: [endpointName, uuid],
    queryFn: async () => (uuid ? ((await getList(uuid)) as PlaceList) : null),
    // 10 minutes
    staleTime: 600_000,
  });

  const loading = (isPending || isFetching) && !error;

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: resourceName, href: '/dashboard/lists' },
        { text: data ? (data.name ?? '') : '', href: '' },
      ]}
      header={<Header variant="h1">{data ? (data.name ?? '') : ''}</Header>}
    >
      {loading || error ? (
        <LoadingOrError loading={loading} error={error} retry={refetch} />
      ) : data ? (
        <ListView list={data} />
      ) : null}
    </DashboardViewLayout>
  );
}

export type ListViewProps = {
  list: PlaceList;
};

export function ListView(props: ListViewProps) {
  const navigate = useNavigate();
  const { list } = props;

  const [now, setNow] = useState(new Date());
  const { sortingField, setSortingField, sortDirection, setSortDirection } = useUrlSortingState(
    'updated_at',
    'desc',
  );
  const [selectedItems, setSelectedItems] = useState<Place[]>([]);

  const remove = useMutation({
    mutationFn: async (data: PlaceRequest) => {
      await removePlacesFromList(list.uuid, data);
    },
    onSuccess: () => {
      setSelectedItems([]);
      setNow(new Date(new Date().getTime() + 1000));
    },
  });

  const removePlaces = () => {
    remove.mutate({
      place_uuids: selectedItems.map((place) => place.uuid),
    });
  };

  return (
    <Grid
      gridDefinition={[{ colspan: { default: 12, s: 12 } }, { colspan: { default: 12, s: 12 } }]}
    >
      <Container header={<Header variant="h2">List Details</Header>}>
        <SpaceBetween direction="vertical" size="s">
          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Name</Box>
              <Box variant="p">{list.name}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Description</Box>
              <Box variant="p">{list.description ?? '-'}</Box>
            </div>
          </ColumnLayout>
        </SpaceBetween>
      </Container>
      <Container>
        <PlaceTable
          variant="embedded"
          now={now}
          setNow={setNow}
          sortingField={sortingField}
          setSortingField={setSortingField}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          query={{
            tokens: [
              {
                propertyKey: 'list_name',
                value: list.name,
                operator: '=',
              },
            ],
            operation: 'and',
          }}
          hideFilter
          additionalActions={
            <Button
              variant="normal"
              iconName="subtract-minus"
              onClick={removePlaces}
              disabled={selectedItems.length === 0}
            >
              Remove from List
            </Button>
          }
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
        />
      </Container>
    </Grid>
  );
}
